import Fuse from 'fuse.js';
import { getKpiResult, getTemporaryKpiResult } from '@/api/kpi';
import { getTelemetryState } from '@/api/telemetry';
import { combineFilters, naturalSort, reverseNaturalSort } from '@/helpers/util';

export function getKpiColumns(columns) {
  return columns.filter((c) => c.type === 'kpi');
}

export function findInTree(tree, searchFn) {
  if (searchFn(tree)) return tree;
  const searchTree = Array.isArray(tree) ? tree : tree.$children;
  if (searchTree) {
    for (let i = 0; i < searchTree.length; i += 1) {
      const child = searchTree[i];
      const found = findInTree(child, searchFn);
      if (found) return found;
    }
  }
  return null;
}

export async function fetch({
  kpi,
  rootNode,
  accountId,
  period,
  offset,
  range,
  scope,
  disableHierarchy,
}) {
  // Running the raw KPI
  if (!rootNode && !disableHierarchy && kpi && kpi.grouping) {
    const kpiParams = { scope };
    if (range) {
      kpiParams.range = range;
    } else {
      kpiParams.offset = offset;
      kpiParams.period = period;
    }
    const { kpi: kpiSaveObject } = kpi.toSaveObject();
    try {
      const results = await getTemporaryKpiResult(accountId, kpiSaveObject, kpiParams);
      const byX = results.byX();
      const data = Object.keys(byX).map((k) => ({
        $id: k,
        [kpi.name]: byX[k].data[0],
      }));
      return Promise.resolve(data);
    } catch (e) {
      return Promise.reject(new Error(e));
    }
  }

  const filters = [scope];

  if (rootNode && rootNode.$drilldown) {
    filters.push(rootNode.$drilldown);
  }

  const params = {
    scope: combineFilters(filters) || undefined,
  };

  try {
    const { data } = await getTelemetryState(accountId, params);
    return Promise.resolve(data);
  } catch (err) {
    return Promise.reject(err);
  }
}

export async function fetchKpi({
  kpi,
  accountId,
  scope,
  period,
  offset,
  range,
}) {
  const kpiParams = {
    view: 'list',
    period,
    offset,
    scope,
    range,
  };

  let results;
  try {
    results = await getKpiResult(accountId, kpi.$id, kpiParams);
  } catch (e) {
    return Promise.reject(new Error(e));
  }
  const byX = results.byX();

  // Flatten results into { $id: results }
  // const data = Object.keys(byX).map((k) => ({
  //   $id: k,
  //   [kpi.name]: byX[k].data[0],
  // })).reduce((acc, curr) => {
  //   acc[curr.$id] = curr[kpi.name];
  //   return acc;
  // }, {});

  const { measurements } = kpi;

  const data = Object.keys(byX).map((k) => {
    const resultData = byX[k].data;
    const datum = { $id: k };
    if (measurements.length === 1) {
      return {
        ...datum,
        [kpi.name]: resultData[0],
      };
    }

    measurements.forEach((m, index) => {
      const [d] = resultData;
      datum[m.label] = {
        y0: d[m.label],
        displayY0: d[`displayY${index}`],
        level0: d[`level${index}`],
      };
    });

    return datum;
  }).reduce((acc, curr) => {
    const { $id, ...rest } = curr;
    acc[$id] = Object.keys(rest).map((k) => ({ label: k, ...rest[k] }));
    return acc;
  }, {});

  return data;
}

const kpiSortFunc = (a, b, column) => {
  const { kpi } = column;
  const aKpi = a[kpi];
  const bKpi = b[kpi];
  if (aKpi === null || aKpi === undefined || aKpi.y0 === undefined) {
    return 1;
  }
  if (bKpi === null || bKpi === undefined || bKpi.y0 === undefined) {
    return -1;
  }

  const aVal = aKpi.y0;
  const bVal = bKpi.y0;

  if (aVal === bVal) return 0;

  if (column.sort === 'asc') {
    if (aVal < bVal) return -1;
    if (aVal > bVal) return 1;
  } else {
    if (aVal < bVal) return 1;
    if (aVal > bVal) return -1;
  }

  return 0;
};

// Recursively flatten model data into rows for displaying
function flattenRows({
  data,
  sortFunction = (d) => d,
  depth = 0,
  rows = [],
}) {
  const sorted = sortFunction(data);
  sorted.forEach((d) => {
    const row = d;
    row.$depth = depth;

    if (Array.isArray(row.$children)) {
      row.$expanded = true;
      rows.push(row);

      flattenRows({
        data: row.$children,
        sortFunction,
        depth: depth + 1,
        rows,
      });
    } else {
      rows.push(row);
    }
  });
  return rows;
}

export function modelDataToRows({
  data,
  columns,
}) {
  let sortFunction = (d) => naturalSort(d, '$id');

  columns.forEach((column) => {
    const { type, property } = column;
    if (column.sort) {
      if (type === 'data' || type === 'identifier') {
        sortFunction = column.sort === 'asc'
          ? (d) => naturalSort(d, property)
          : (d) => reverseNaturalSort(d, property);
      } else if (type === 'kpi') {
        // KPI sort function
        sortFunction = (d) => d.sort((a, b) => kpiSortFunc(a, b, column));
      }
    }
  });

  const flattened = flattenRows({
    data,
    sortFunction,
  });

  return flattened.map((r) => {
    // Only keep the data that is needed from the columns
    const row = {
      $id: r.$id,
      $nodeId: r.$nodeId,
      $drilldown: r.$drilldown,
      $tableMeta: {
        treeDepth: r.$depth,
        nodeId: r.$nodeId,
        expanded: r.$expanded,
        hasChildren: r.$hasChildren,
        placeholder: r.$placeholder,
        loading: false,
      },
    };
    columns.forEach((col) => {
      const name = col.kpi || col.property;
      row[name] = r[name];

      if (col.kpi && r[name] && r[name].empty) {
        row.$tableMeta.filter = true;
      }
    });
    return row;
  }).filter((r) => !r.$tableMeta.filter);
}

export function filterRowData({
  tableRows,
  kpiFilters = {},
  filterText,
  columns,
}) {
  const {
    green, amber, red, showMin, showMax,
  } = kpiFilters;

  let currentRows = tableRows;
  if (filterText) {
    const fuse = new Fuse(tableRows, {
      keys: columns.map((c) => c.property).filter((c) => c !== undefined),
      includeScore: true,
      ignoreLocation: true,
      useExtendedSearch: true,
      findAllMatches: true,
      shouldSort: false,
      threshold: 0, // Lower the threshold to filter out less accurate results
    });

    const searchResults = fuse.search(filterText);
    currentRows = searchResults.map(({ item }) => item);
  }

  return currentRows.filter((row) => {
    // KPI traffic light filtering
    const kpiColumns = getKpiColumns(columns);

    for (let i = 0; i < kpiColumns.length; i += 1) {
      const kpiCol = kpiColumns[i];
      const value = row[kpiCol.label];
      if (kpiCol && value) {
        if (green === false || amber === false || red === false) {
          const { level0: level } = value;
          if (green === false && level === 'success') {
            return false;
          } if (amber === false && level === 'warning') {
            return false;
          } if (red === false && level === 'error') {
            return false;
          }
        }
        if (!showMin || !showMax) {
          const { y0 } = value;
          if (!showMin && y0 === 0) return false;
          if (!showMax && y0 === 100) return false;
        }
      }
    }

    return true;
  });
}

export function migrateStateFromOldList(oldSettings, properties) {
  // Construct an array of the new style settings from the old ones
  const oldArray = Object.keys(oldSettings)
    .map((k) => ({ prop: k, ...oldSettings[k] }))
    .filter((col) => col.prop !== '$kpi' && col.visible);
  oldArray.sort((a, b) => a.order - b.order);

  const columns = oldArray.map((col) => {
    const prop = properties.find((p) => p.name === col.prop);
    if (prop) {
      return {
        type: prop.name === '$id' ? 'identifier' : 'data',
        property: prop.name,
        label: prop.alias,
        date: prop.isTs,
      };
    }
    return null;
  }).filter((col) => col !== null);

  return { columns };
}
